import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { IconSize } from '@lux/icons';
import { Flag, FlagType } from '@lux/flags';
import { PlusEarlyAccessBadge } from '@browsing/plus';

import { Campaign } from '@shared/types/mylounge';
import { useDeliveryFlagVariant } from '@shared/abtests';
import { RootState } from '@client/redux/types';
import { trackOpenCampaignClick } from '@client/helpers/tracking';
import { buildCampaignUrl } from '@client/helpers/campaignUrl';
import { getTrackingData } from '@client/redux/modules/tracking/selectors';
import { makeGetCampaignCategories } from '@client/redux/modules/myloungeMetadata/selectors';
import { getCampaignMobileImage } from '@client/helpers/images';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { useDeliveryLabel } from '@client/components/mylounge/hooks/use-delivery-label';
import { ShortenedDelivery } from '@client/components/mylounge/campaigns/delivery-label.styles';

import CampaignLabel from '../campaign-label';
import * as S from './open-campaign.styles';

const LABEL_HEIGHT = 60;
const PREVIEW_LABEL_HEIGHT = LABEL_HEIGHT + 35;

interface Props {
    campaign: Campaign;
    index: number;
    isPlusEnabled: boolean;
    minHeight: number;
}

export const OpenCampaign: FunctionComponent<Props> = ({
    campaign,
    index,
    isPlusEnabled,
    minHeight,
}) => {
    const categoryTabId = useSelector(getCategoryTabId);
    const tracking = useSelector(getTrackingData);
    const getCampaignSubLineCategory = useMemo(makeGetCampaignCategories, []);
    const isNewFastDeliveryFlag: boolean = useDeliveryFlagVariant();
    const sublineCategory = useSelector((state: RootState) =>
        getCampaignSubLineCategory(state, campaign.campaignId),
    );
    const imageUrl = useMemo(() => {
        return getCampaignMobileImage(campaign);
    }, [campaign]);
    const { isSpecialDeliveryBanner, showDeliveryLabel } = useDeliveryLabel(campaign);
    const trackCampaignOverview = useCallback(() => {
        localStorage.setItem('navigationFrom', 'myLounge');
        trackOpenCampaignClick(campaign, {
            categoryTabId,
            position: `${index}|1`,
            visitorID: tracking?.visitorID || null,
        });
    }, [campaign, categoryTabId, index, tracking?.visitorID]);

    const campaignId = `open-${campaign.campaignId}`;

    const destination = buildCampaignUrl(campaign.campaignId, categoryTabId);

    const withDeliveryLabel = campaign?.deliveryPromise?.type && campaign?.deliveryPromise?.text;
    const coverWithoutLogo = !campaign.hasLogo;
    const isEarlyAccess = campaign.plusEarlyAccess;

    return (
        <S.Container id={campaignId} data-position={`${index}|1`}>
            <S.Wrapper $minHeight={minHeight + LABEL_HEIGHT} $relative>
                <S.Wrapper $minHeight={minHeight + PREVIEW_LABEL_HEIGHT}>
                    <S.Image
                        as="a"
                        onClick={trackCampaignOverview}
                        href={destination}
                        $minHeight={minHeight}
                        $backgroundImage={imageUrl}
                        $withoutLogo={coverWithoutLogo}
                    >
                        {isPlusEnabled && isEarlyAccess && (
                            <PlusEarlyAccessBadge size={IconSize.S} />
                        )}
                        {isNewFastDeliveryFlag && showDeliveryLabel && (
                            <ShortenedDelivery>
                                <Flag flagStyle={FlagType.NEON}>
                                    {campaign.deliveryPromise.text}
                                </Flag>
                            </ShortenedDelivery>
                        )}
                    </S.Image>
                    {coverWithoutLogo && (
                        <S.TitleLabel $withDeliveryLabel={!!withDeliveryLabel}>
                            {campaign.name}
                        </S.TitleLabel>
                    )}
                    <S.Label>
                        <CampaignLabel
                            isSpecialDeliveryBanner={isSpecialDeliveryBanner}
                            showDeliveryLabel={showDeliveryLabel}
                            campaign={campaign}
                            categorySubline={sublineCategory}
                        />
                    </S.Label>
                </S.Wrapper>
            </S.Wrapper>
        </S.Container>
    );
};

export default OpenCampaign;
